import React, { useContext, useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { DropdownContext } from "./Dropdown";
import { PopupContext } from "../Popup/PopupContext";

/**
 * DropdownMenu — The floating container that holds dropdown items.
 *
 * Reads `open` from DropdownContext and conditionally renders.
 * Keeps the menu mounted briefly on close to play the exit animation.
 *
 * @param {Object}  customStyle   - Inline styles for the menu container
 * @param {string}  customClasses - Additional CSS classes
 * @param {boolean} usePortal     - Render via portal into document.body to escape
 *                                  overflow:hidden/auto clipping (e.g. inside popups).
 *                                  Positions using the trigger's getBoundingClientRect().
 * @param {"left"|"right"} align  - Which edge of the trigger to align to (default: "left")
 */
export const DropdownMenu = ({ children, customStyle = {}, customClasses = "", usePortal = false, align = "left", innerStyle = {} }) => {
  const { open, triggerRef, menuRef, zIndex } = useContext(DropdownContext);
  const [rect, setRect] = useState(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Drive mount/unmount with a brief exiting phase for the close animation
  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setIsExiting(false);
    } else if (shouldRender) {
      setIsExiting(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure and keep trigger position in sync for portaled menus.
  useEffect(() => {
    if (!(open && usePortal && triggerRef?.current)) return;

    const updateRect = () => {
      if (!triggerRef?.current) return;
      setRect(triggerRef.current.getBoundingClientRect());
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [open, usePortal, triggerRef]);

  // Prevent inside mousedown events from bubbling to document and triggering close
  useEffect(() => {
    if (!shouldRender || !menuRef?.current) return;
    const handleMenuMouseDown = (e) => {
      e.stopPropagation();
    };
    const el = menuRef.current;
    el.addEventListener("mousedown", handleMenuMouseDown);
    return () => {
      el.removeEventListener("mousedown", handleMenuMouseDown);
    };
  }, [shouldRender, menuRef]);

  const handleAnimationEnd = () => {
    if (isExiting) {
      setShouldRender(false);
      setRect(null);
    }
  };

  const portalZIndex = zIndex ?? 1100;
  const popupCtxValue = useMemo(() => ({ zIndex: portalZIndex }), [portalZIndex]);

  if (!shouldRender) return null;

  // Wait for rect measurement before rendering portal — avoids a brief flash at (0,0)
  if (usePortal && !rect) return null;

  const exitClass = isExiting ? 'kb-dropdown-menu--exiting' : '';

  if (usePortal && rect) {
    const gap = 4;
    const edgePadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap - edgePadding;
    const spaceAbove = rect.top - gap - edgePadding;
    const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = openAbove ? spaceAbove : spaceBelow;
    const portalStyle = {
      position: "fixed",
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + gap, top: "auto" }
        : { top: rect.bottom + gap }),
      ...(align === "right"
        ? { right: window.innerWidth - rect.right, left: "auto" }
        : { left: rect.left }),
      maxHeight,
      overflowY: "auto",
      zIndex: zIndex ?? 1100, // --z-tooltip
    };

    return ReactDOM.createPortal(
      <PopupContext.Provider value={popupCtxValue}>
        <div
          ref={menuRef}
          className={`kb-dropdown-menu ${exitClass} ${customClasses}`}
          style={{ ...portalStyle, ...customStyle }}
          onAnimationEnd={handleAnimationEnd}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="kb-dropdown-menu__content" style={innerStyle}>{children}</div>
        </div>
      </PopupContext.Provider>,
      document.body
    );
  }

  return (
    <div
      ref={menuRef}
      className={`kb-dropdown-menu ${exitClass} ${customClasses}`}
      style={{ ...customStyle }}
      onAnimationEnd={handleAnimationEnd}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="kb-dropdown-menu__content" style={innerStyle}>{children}</div>
    </div>
  );
};
